import * as React from 'react';
import { useState } from 'react';
import { Decimal, ZERO } from '@insio/decimal';
import type { IDocumentExchangeRate } from '@insio/shared-types';
import { EditorProps } from '../types/editor-props';
import {
  EditInvoiceTotalsModal,
  type InvoiceTotalsFormData,
  type InvoiceTotalsPaymentStatus
} from '../../../../modules/modals/EditInvoiceTotalsModal';
import {
  useInvoiceAccountingCurrencyCode,
  useInvoiceCurrencyCode,
  useInvoiceExpectedTotalsFromLines,
  useInvoiceExpectedTotalsFromVatSummary
} from '../../../../providers/InvoiceProvider';
import { InvoiceTotalsFormatter } from '../formatters/InvoiceTotalsFormatter';

interface InvoiceTotalsValue {
  totalAmountNoVat?: Decimal | null;
  totalVatAmount?: Decimal | null;
  totalAmountInclVat?: Decimal | null;
  paymentStatus?: InvoiceTotalsPaymentStatus | null;
  paidAmount?: Decimal | null;
  accountingExchangeRate?: IDocumentExchangeRate | null;
  roundingAmount?: Decimal | null;
  accountingAmount?: Decimal | null;
  accountingAmountNoVat?: Decimal | null;
  accountingTotalVatAmount?: Decimal | null;
  accountingCurrencyCode?: string | null;
}

function toDecimalOrZero(value: Decimal | null | undefined): Decimal {
  return value instanceof Decimal ? value : ZERO;
}

/**
 * Invoice totals editor: the summary card that opens `EditInvoiceTotalsModal`.
 * Shows the paid status and amount but never saves them.
 */
export function InvoiceTotalsEditor({ value, onChange, onCommit, onCancel, errors }: EditorProps) {
  const [open, setOpen] = useState(true);
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  const accountingCurrencyCode = useInvoiceAccountingCurrencyCode();
  const expectedTotalsFromVatSummary = useInvoiceExpectedTotalsFromVatSummary();
  const expectedTotalsFromLines = useInvoiceExpectedTotalsFromLines();

  const data = (value ?? {}) as InvoiceTotalsValue;

  // Surface the rate when the invoice currency differs from the party's
  // accounting currency. Also surface it when a rate is already persisted,
  // so users can edit or clear values saved under prior currency settings.
  const currenciesDiffer =
    accountingCurrencyCode != null &&
    invoiceCurrencyCode != null &&
    accountingCurrencyCode !== invoiceCurrencyCode;
  const showAccountingExchangeRate = currenciesDiffer || data.accountingExchangeRate != null;

  const handleSave = async (next: InvoiceTotalsFormData) => {
    if (onCommit) {
      await onCommit(next);
    } else {
      onChange(next);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onCancel?.();
  };

  return (
    <>
      <div className='w-full'>
        <InvoiceTotalsFormatter value={data} errors={errors} />
      </div>
      <EditInvoiceTotalsModal
        open={open}
        onClose={handleClose}
        onSave={handleSave}
        currentTotalNoVat={toDecimalOrZero(data.totalAmountNoVat)}
        currentTotalVatAmount={toDecimalOrZero(data.totalVatAmount)}
        currentTotalInclVat={toDecimalOrZero(data.totalAmountInclVat)}
        currentPaymentStatus={data.paymentStatus ?? null}
        currentPaidAmount={data.paidAmount ?? null}
        currentAccountingExchangeRate={data.accountingExchangeRate ?? null}
        currentRoundingAmount={data.roundingAmount ?? null}
        currentAccountingAmount={data.accountingAmount ?? null}
        currentAccountingAmountNoVat={data.accountingAmountNoVat ?? null}
        currentAccountingTotalVatAmount={data.accountingTotalVatAmount ?? null}
        currentAccountingCurrencyCode={data.accountingCurrencyCode ?? accountingCurrencyCode}
        showAccountingExchangeRate={showAccountingExchangeRate}
        currencyCode={invoiceCurrencyCode}
        accountingCurrencyCode={accountingCurrencyCode}
        expectedTotalsFromVatSummary={expectedTotalsFromVatSummary}
        expectedTotalsFromLines={expectedTotalsFromLines}
        errors={errors}
      />
    </>
  );
}

(InvoiceTotalsEditor as React.FC).displayName = 'InvoiceTotalsEditor';
